/**
 * DataSourceManager - Central UI for managing all data sources
 * 
 * CHANGE HISTORY:
 * - 251201_0440 danielbeer: Created data source manager UI
 * 
 * PURPOSE:
 * Single interface for creating and managing data sources across OpCentral.
 * Used by Aggregatr, Actr, and any tool needing external data.
 */

import React, { useState } from 'react';
import { FileMakerODataConfig as FMODataConfigComponent } from './datasources/FileMakerODataConfig';
import { FileMakerDataAPIConfig as FMDataAPIConfigComponent } from './datasources/FileMakerDataAPIConfig';
import { FileMakerScriptConfig as FMScriptConfigComponent } from './datasources/FileMakerScriptConfig';
import type { 
  FileMakerODataConfig as FMODataConfig,
  FileMakerDataAPIConfig as FMDataAPIConfig,
  FileMakerScriptConfig as FMScriptConfig
} from '../types/FileMakerTypes';
import type { DataSourceConfig } from '../DataSourceManager';
import './DataSourceManager.css';

export type DataSourceType = 
  | 'filemaker-odata'
  | 'filemaker-dataapi'
  | 'filemaker-script'
  | 'rest-api'
  | 'sql'
  | 'graphql'
  | 'websocket';

export interface DataSourceManagerProps {
  /** Existing source to edit (optional) */
  sourceId?: string;
  
  /** Initial source type (for creation) */
  initialType?: DataSourceType;
  
  /** Callback when source is saved */
  onSave: (source: DataSourceConfig) => void;
  
  /** Callback when cancelled */
  onCancel?: () => void;
  
  /** Mode: create or edit */
  mode?: 'create' | 'edit';
}

export function DataSourceManager({
  sourceId,
  initialType,
  onSave,
  onCancel,
  mode = 'create'
}: DataSourceManagerProps) {
  const [selectedType, setSelectedType] = useState<DataSourceType | null>(initialType || null);
  const [sourceName, setSourceName] = useState('');
  const [sourceConfig, setSourceConfig] = useState<any>({});
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleTypeSelect = (type: DataSourceType) => {
    setSelectedType(type);
    setSourceConfig({});
    setTestResult(null);
  };

  const handleSave = () => {
    if (!selectedType || !sourceName) {
      alert('Please provide a name and select a source type');
      return;
    }

    const source: DataSourceConfig = {
      name: sourceName,
      type: selectedType as any,
      config: sourceConfig,
      entityTypes: ['record'],
      enabled: true
    };

    onSave(source);
  };

  const handleTest = async () => {
    setTestResult({ success: true, message: 'Connection test successful!' });
    // TODO: Implement actual test via DataSourceManager.execute()
  };

  return (
    <div className="data-source-manager">
      <div className="dsm-content">
        {/* Step 1: Name */}
        <div className="dsm-section">
          <label className="dsm-label">Source Name</label>
          <input
            type="text"
            value={sourceName}
            onChange={(e) => setSourceName(e.target.value)}
            placeholder="e.g., Contacts Database"
            className="dsm-input"
          />
        </div>

        {/* Step 2: Type Selection */}
        {!selectedType && (
          <div className="dsm-section">
            <label className="dsm-label">Select Data Source Type</label>
            <div className="source-type-grid">
              <SourceTypeCard
                icon="📊"
                title="FileMaker OData"
                description="Connect via OData REST API"
                onClick={() => handleTypeSelect('filemaker-odata')}
              />
              <SourceTypeCard
                icon="🗄️"
                title="FileMaker Data API"
                description="Native FileMaker Data API"
                onClick={() => handleTypeSelect('filemaker-dataapi')}
              />
              <SourceTypeCard
                icon="⚡"
                title="FileMaker Script"
                description="Execute FileMaker scripts"
                onClick={() => handleTypeSelect('filemaker-script')}
              />
              <SourceTypeCard
                icon="🌐"
                title="REST API"
                description="Generic REST endpoint"
                onClick={() => handleTypeSelect('rest-api')}
                disabled
              />
              <SourceTypeCard
                icon="🗃️"
                title="SQL Database"
                description="MySQL, PostgreSQL, etc."
                onClick={() => handleTypeSelect('sql')}
                disabled
              />
              <SourceTypeCard
                icon="🔷"
                title="GraphQL"
                description="GraphQL endpoint"
                onClick={() => handleTypeSelect('graphql')}
                disabled
              />
            </div>
          </div>
        )}

        {/* Step 3: Configuration */}
        {selectedType && (
          <div className="dsm-section">
            <div className="dsm-section-header">
              <label className="dsm-label">Configuration</label>
              <button 
                onClick={() => setSelectedType(null)} 
                className="btn-link"
              >
                ← Change Type
              </button>
            </div>

            {selectedType === 'filemaker-odata' && (
              <FMODataConfigComponent
                config={sourceConfig as FMODataConfig}
                onChange={(updates: Partial<FMODataConfig>) => setSourceConfig({ ...sourceConfig, ...updates })}
                onTest={handleTest}
              />
            )}

            {selectedType === 'filemaker-dataapi' && (
              <FMDataAPIConfigComponent
                config={sourceConfig as FMDataAPIConfig}
                onChange={(updates: Partial<FMDataAPIConfig>) => setSourceConfig({ ...sourceConfig, ...updates })}
                onTest={handleTest}
              />
            )}

            {selectedType === 'filemaker-script' && (
              <FMScriptConfigComponent
                config={sourceConfig as FMScriptConfig}
                onChange={(updates: Partial<FMScriptConfig>) => setSourceConfig({ ...sourceConfig, ...updates })}
                onTest={handleTest}
              />
            )}
          </div>
        )}

        {/* Test Result */}
        {testResult && (
          <div className={`dsm-test-result ${testResult.success ? 'success' : 'error'}`}>
            {testResult.success ? '✅' : '❌'} {testResult.message}
          </div>
        )}

        {/* Footer Actions */}
        <div className="dsm-footer">
          {onCancel && (
            <button onClick={onCancel} className="btn btn-secondary">
              Cancel
            </button>
          )}
          <button 
            onClick={handleSave} 
            className="btn btn-primary"
            disabled={!selectedType || !sourceName}
          >
            {mode === 'create' ? 'Create Source' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface SourceTypeCardProps {
  icon: string;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

function SourceTypeCard({ icon, title, description, onClick, disabled }: SourceTypeCardProps) {
  return (
    <button
      className={`source-type-card ${disabled ? 'disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="source-type-icon">{icon}</div>
      <div className="source-type-title">{title}</div>
      <div className="source-type-description">{description}</div>
      {disabled && <div className="source-type-badge">Coming Soon</div>}
    </button>
  );
}
